// react
import { useState } from "react";
import lscache from "lscache";

// redux
import { bindActionCreators } from "redux";
import { connect, useDispatch } from "react-redux";
import { SHOW_APP_MESSAGE } from "~/redux/actions/app";

// compos
import { Popconfirm, Input, Modal, Form, AutoComplete } from "antd";
import { FormOutlined, MessageFilled, DeleteOutlined } from "@ant-design/icons";
import IconLink from "~/components/app/IconLink";
import UserAvatar from "~/components/user/UserAvatar";
import UserLink from "~/components/user/UserLink";
import CommentForm from "~/components/comment/CommentForm";
import CommentVote from "~/components/comment/CommentVote";
import RichContent from "~/components/app/RichContent";
import TextContent from "~/components/app/TextContent";
import LastModifiedTag from "~/components/app/LastModifiedTag";

// add report action
import SiteReportAction from "~/components/site/SiteReportAction";
import TimeAgoTag from "~/components/app/TimeAgoTag";

// utils
import { hasAnyRole } from "~/utils/user";

const CommentItem = (props) => {
  const { comment } = props;
  const [state, setState] = useState({
    form: null,
  });

  const dataSource = lscache.get("commentReasonHistory")
    ? lscache.get("commentReasonHistory")
    : [];
  const dispatch = useDispatch();

  const hasParent = !!comment.ancestor_id;
  const isOwnner = props.checkOwner(comment);
  const isAuthor = props.checkAuthor ? props.checkAuthor(comment.user) : false;
  const isCollaborationNotFollowed = props.checkCollaboration();
  const isDeleted = comment.deleted;
  const isModified = comment.modified_at && comment.modified_at !== comment.published_at;
  const isAdmin = hasAnyRole(props.user, ["admin"]);
  const isRichText = /.*<.*?>.*<.*?>.*/.test(comment.content || "");
  const wrapperClass = hasParent ? "sub-comment" : "root-comment";
  const handleReply = () => {
    if (isCollaborationNotFollowed) {
      dispatch({
        type: SHOW_APP_MESSAGE,
        message: { text: "请先加入该共学", level: "info" },
      });
      return;
    }
    setState({ form: "reply" });
  };

  const handleEdit = () => {
    setState({ form: "edit" });
  };

  const handleCancel = () => {
    setState({ form: null });
  };

  const handleOwnerDelete = () => {
    props.onDelete(comment);
  };

  const handleAdminDelete = (reason) => {
    props.onDelete(comment, reason);
  };

  const setReason = (reason) => {
    if (!lscache.get("commentReasonHistory")) {
      lscache.set("commentReasonHistory", [reason]);
    } else {
      let reasonArr = lscache.get("commentReasonHistory");
      if (reasonArr.indexOf(reason) === -1) {
        reasonArr.push(reason);
        lscache.set("commentReasonHistory", reasonArr);
      }
    }
  };

  // 管理删除确认对话框
  const showDeleteConfirmModal = () => {
    let reason = null;
    Modal.confirm({
      title: "确认删除评论并扣除电量和经验？",
      content: (
        <Form>
          <Form.Item>
            <AutoComplete
              style={{ width: "100%" }}
              dataSource={dataSource}
              placeholder="删除原因（选填）"
              filterOption={(inputValue, option) =>
                option.props.children
                  .toUpperCase()
                  .indexOf(inputValue.toUpperCase()) !== -1
              }
              onChange={(value) => {
                reason = value;
              }}
            />
          </Form.Item>
        </Form>
      ),
      onOk() {
        reason && setReason(reason);
        handleAdminDelete(reason);
      },
    });
  };

  const commentAside = (
    <div className="aside">
      <UserAvatar user={comment.user} showLevel />
    </div>
  );
  const editForm = (
    <div className="form-update">
      <CommentForm
        postId={comment.post_id}
        parentId={comment.parent_id}
        comment={comment}
        title={comment.post_title}
        mode="update"
        onCancel={handleCancel}
      />
    </div>
  );

  const replyForm = (
    <div className="form-reply">
      <CommentForm
        postId={comment.post_id}
        parentId={comment.id}
        title={comment.post_title}
        mode="reply"
        onCancel={handleCancel}
      />
    </div>
  );
  const replyList = comment.replies.map((reply) => {
    return (
      <CommentItem
        comment={reply}
        key={reply.id}
        user={props.user}
        checkOwner={props.checkOwner}
        checkAuthor={props.checkAuthor}
        checkCollaboration={props.checkCollaboration}
        onDelete={props.onDelete}
      />
    );
  });
  const commentDate = (
    <a
      href={`#comment-${comment.id}`}
      className="pull-right text-sm text-muted"
    >
      <TimeAgoTag time={comment.published_at} />
    </a>
  );
  const replyButton = (
    <span>
      <IconLink icon={<MessageFilled />} text="回复" onClick={handleReply} />
    </span>
  );
  const voteButton = (
    <span>
      <CommentVote id={comment.id} votes={comment.votes_count} />
    </span>
  );
  const editButton = (
    <span>
      <IconLink icon={<FormOutlined />} text="编辑" onClick={handleEdit} />
    </span>
  );

  // 适用于评论作者的删除按钮
  const deleteButtonForOwner = (
    <Popconfirm
      title="确认删除评论？"
      placement="topLeft"
      okText="删除"
      cancelText="取消"
      onConfirm={handleOwnerDelete}
    >
      <span>
        <IconLink icon={<DeleteOutlined />} text="删除" />
      </span>
    </Popconfirm>
  );

  // 适用于管理员的删除按钮
  const deleteButtonForAdmin = (
    <span>
      <IconLink
        icon={<DeleteOutlined />}
        text="管理删除"
        onClick={showDeleteConfirmModal}
      />
    </span>
  );

  const renderDeleteButton = () => {
    if (isOwnner) {
      return deleteButtonForOwner;
    } else if (isAdmin) {
      return deleteButtonForAdmin;
    } else {
      return null;
    }
  };

  const commentActions = (
    <div className="actions">
      {voteButton}
      {props.user && replyButton}
      {isOwnner && editButton}
      <span>
        <SiteReportAction contentType={"Comment"} contentId={comment.id} />
      </span>
      {renderDeleteButton()}
    </div>
  );

  const renderContent = () => {
    if (isDeleted) {
      if (comment.deleted_mode == "admin_delete") {
        return (
          <div className="comment-content deleted">
            该评论因违反
            <a href="https://eleduck.com/posts/6GzfGe" target="_blank">
              《社区规则》
            </a>
            ，已被社区管理员删除
          </div>
        );
      } else {
        return <div className="comment-content deleted">用户已删除此评论</div>;
      }
    } else if (isRichText) {
      return <RichContent html={comment.content} />;
    } else {
      return <TextContent text={comment.content} />;
    }
  };

  return (
    <div className={wrapperClass} id={`comment-${comment.id}`}>
      <div className="comment">
        {!hasParent && commentAside}
        <div className="body">
          <div className="heading mb-15">
            <div className="meta">
              {commentDate}
              <UserLink
                user={comment.user}
                avatar={hasParent}
                isAuthor={isAuthor}
              />
              {comment.parent_user && (
                <div className="inline-flex gap-10 ml-10">
                  <span>回复</span>
                  <UserLink
                    user={comment.parent_user}
                    avatar={hasParent}
                    isAuthor={
                      props.checkAuthor
                        ? props.checkAuthor(comment.parent_user)
                        : false
                    }
                  />
                </div>
              )}
            </div>
          </div>

          {renderContent()}

          {!isDeleted && isModified && <p><LastModifiedTag time={comment.modified_at} /></p>}

          {!isDeleted && commentActions}
        </div>
      </div>
      <div className="extra">
        {state.form === "reply" && replyForm}
        {state.form === "edit" && editForm}
        {replyList}
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  user: state.user.user,
});

const mapDispatchToProps = (dispatch) => bindActionCreators({}, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(CommentItem);
